import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useBase } from "@/contexts/BaseContext";
import { MapPin, ArrowRight, Loader2 } from "lucide-react";

const COLS = [
  { key: "horses", label: "Horses" },
  { key: "injured", label: "Injured", danger: true },
  { key: "staff", label: "Staff" },
  { key: "vehicles", label: "Vehicles" },
  { key: "salary_employees", label: "Payroll" },
  { key: "low_feed", label: "Low feed", danger: true },
];

/**
 * A compact side-by-side comparison of every base, shown on the dashboard
 * for all-access users (master/admin/executive) when more than one base
 * exists. Tapping a base switches the whole app into that base.
 */
export function BaseComparisonCards() {
  const { allAccess, bases, changeBase } = useBase();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const show = allAccess && bases.length > 1;

  useEffect(() => {
    if (!show) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/bases/overview");
        if (!alive) return;
        setRows(data.bases || []);
        setTotals(data.totals || null);
      } catch (_e) { /* non-fatal */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [show]);

  if (!show) return null;

  return (
    <Card className="rounded-xl" data-testid="dashboard-base-comparison">
      <CardHeader className="pb-3 flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[hsl(var(--ap-saddle))]" /> Bases at a glance
        </CardTitle>
        <Button asChild size="sm" variant="ghost" className="text-xs" data-testid="dashboard-base-comparison-more">
          <Link to="/bases/overview">Full overview <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-[0.12em] text-muted-foreground border-b">
                  <th className="text-left py-2 pr-3">Base</th>
                  {COLS.map((c) => <th key={c.key} className="text-right py-2 px-3 whitespace-nowrap">{c.label}</th>)}
                  <th className="py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ base, metrics }) => (
                  <tr key={base.id} className="border-b last:border-0 hover:bg-accent/50" data-testid={`dashboard-base-row-${base.code || base.id}`}>
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">
                      {base.name}{base.is_primary ? <span className="ml-1 text-[10px] text-muted-foreground">(primary)</span> : null}
                    </td>
                    {COLS.map((c) => (
                      <td key={c.key} className={`py-2 px-3 text-right tabular-nums ${c.danger && metrics[c.key] > 0 ? "text-destructive font-medium" : ""}`}>
                        {metrics[c.key] ?? 0}
                      </td>
                    ))}
                    <td className="py-2 pl-3 text-right">
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        data-testid={`dashboard-base-open-${base.code || base.id}`}
                        onClick={() => changeBase(base.id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
                {totals && (
                  <tr className="font-semibold">
                    <td className="py-2 pr-3">All bases</td>
                    {COLS.map((c) => (
                      <td key={c.key} className="py-2 px-3 text-right tabular-nums">{totals[c.key] ?? 0}</td>
                    ))}
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
